import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { chevronDownIcon } from "@progress/kendo-svg-icons";
import { toast } from "react-toastify";

import { Tile } from "components/UIComponents/Tile";
import { TileWrapper } from "components/UIComponents/TileWrapper";
import SubmitButton from "components/common/SubmitButton";
import { fetchConsultantRefs } from "services/supplierService";
import { getPaymentProfile, updatePaymentProfile } from "services/mattersService";

const DRAFT_KEY = "draft";

const PaymentProfileContext = createContext(null);

const blankRow = (key) => ({
  key,
  supplierRef: null,
  paymentPercentage: null,
});

const toRows = (data) =>
  (data || [])
    .filter((item) => item.active !== false)
    .map((item, index) => ({
      key: item.matterPaymentProfileId ?? `saved-${index}`,
      supplierRef: item.supplierRef ?? null,
      paymentPercentage: item.paymentPercentage ?? null,
    }));

const NameCell = ({ dataItem }) => {
  const { consultants, consultantByRef, rows, onConsultantChange } = useContext(
    PaymentProfileContext,
  );
  const taken = new Set(
    rows.filter((row) => row.key !== dataItem.key).map((row) => row.supplierRef),
  );

  return (
    <td>
      <ComboBox
        data={consultants.filter((item) => !taken.has(item.supplierRef))}
        textField="display"
        dataItemKey="supplierRef"
        svgIcon={chevronDownIcon}
        placeholder="Select Consultant"
        style={{ width: "280px" }}
        value={consultantByRef.get(dataItem.supplierRef) ?? null}
        onChange={(event) => onConsultantChange(dataItem, event.value)}
      />
    </td>
  );
};

const PercentageCell = ({ dataItem }) => {
  const { onPercentageChange } = useContext(PaymentProfileContext);

  return (
    <td>
      <NumericTextBox
        value={dataItem.paymentPercentage ?? null}
        format={{ maximumFractionDigits: 2 }}
        min={0}
        max={100}
        spinners={false}
        disabled={!dataItem.supplierRef}
        className="right-align"
        style={{ width: "90px" }}
        onChange={(event) => onPercentageChange(dataItem, event.value)}
      />
    </td>
  );
};

const PaymentProfileTab = ({ matterId }) => {
  const [consultants, setConsultants] = useState([]);
  const [rows, setRows] = useState([]);
  const [removedRefs, setRemovedRefs] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const nextKey = useRef(0);

  useEffect(() => {
    let cancelled = false;
    fetchConsultantRefs().then((data) => {
      if (!cancelled) setConsultants(data || []);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!matterId) return undefined;
    let cancelled = false;
    getPaymentProfile(matterId).then((response) => {
      if (cancelled) return;
      setRows(toRows(response?.data?.data));
      setRemovedRefs([]);
    });
    return () => {
      cancelled = true;
    };
  }, [matterId]);

  const consultantByRef = useMemo(
    () => new Map((consultants || []).map((item) => [item.supplierRef, item])),
    [consultants],
  );

  const gridRows = useMemo(() => [...rows, blankRow(DRAFT_KEY)], [rows]);

  const handleConsultantChange = useCallback((row, consultant) => {
    const supplierRef = consultant?.supplierRef ?? null;

    if (row.key === DRAFT_KEY) {
      if (!supplierRef) return;
      nextKey.current += 1;
      setRows((current) => [
        ...current,
        { ...blankRow(`new-${nextKey.current}`), supplierRef },
      ]);
      return;
    }

    if (!supplierRef) {
      if (row.supplierRef) {
        setRemovedRefs((current) => [...current, row.supplierRef]);
      }
      setRows((current) => current.filter((item) => item.key !== row.key));
      return;
    }

    setRows((current) =>
      current.map((item) =>
        item.key === row.key ? { ...item, supplierRef } : item,
      ),
    );
  }, []);

  const handlePercentageChange = useCallback((row, value) => {
    if (row.key === DRAFT_KEY) return;
    setRows((current) =>
      current.map((item) =>
        item.key === row.key ? { ...item, paymentPercentage: value } : item,
      ),
    );
  }, []);

  const handleSave = async () => {
    if (!matterId) return;
    const selectedRefs = new Set(rows.map((row) => row.supplierRef));
    const payload = [
      ...rows.map((row) => ({
        supplierRef: row.supplierRef,
        paymentPercentage: row.paymentPercentage ?? 0,
        active: true,
      })),
      ...removedRefs
        .filter((supplierRef) => !selectedRefs.has(supplierRef))
        .map((supplierRef) => ({
          supplierRef,
          paymentPercentage: 0,
          active: false,
        })),
    ];

    setIsSaving(true);
    try {
      const response = await updatePaymentProfile(matterId, payload);
      setRows(toRows(response?.data?.data));
      setRemovedRefs([]);
      toast.success("Payment profile updated successfully");
    } finally {
      setIsSaving(false);
    }
  };

  const cellContext = useMemo(
    () => ({
      consultants,
      consultantByRef,
      rows,
      onConsultantChange: handleConsultantChange,
      onPercentageChange: handlePercentageChange,
    }),
    [consultants, consultantByRef, rows, handleConsultantChange, handlePercentageChange],
  );

  return (
    <TileWrapper>
      <div className="d-flex flex-column dialog-tab-height">
        <Tile className="px-4 pt-2 pb-3 d-flex flex-column flex-grow-1 overflow-hidden">
          <PaymentProfileContext.Provider value={cellContext}>
            <div className="mt-2 flex-grow-1" style={{ minHeight: 0 }}>
              <Grid data={gridRows} dataItemKey="key" style={{ height: "100%" }}>
                <GridColumn
                  field="supplierRef"
                  title="Name"
                  width="320px"
                  cell={NameCell}
                />
                <GridColumn
                  field="paymentPercentage"
                  title="Percentage"
                  cell={PercentageCell}
                />
              </Grid>
            </div>
          </PaymentProfileContext.Provider>

          <div className="d-flex justify-content-end mt-3">
            <SubmitButton
              type="button"
              className="bgBtnSecondary fw-bold"
              style={{ minWidth: "11.5rem" }}
              onClick={handleSave}
              isLoading={isSaving}
              loaderClassName=""
            >
              Update Payment Profile
            </SubmitButton>
          </div>
        </Tile>
      </div>
    </TileWrapper>
  );
};

export default PaymentProfileTab;
